import { Inject, inject, Injectable, signal } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { catchError, combineLatest, filter, first, interval, map, Observable, of, startWith } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { LoginState, LoginStateService } from '@client-monorepo/applets/auth';
import { MessageService, StorageService } from '@client-monorepo/common/utilities';
import { NgxHybridService } from '@digipay/ngx-hybrid-service';
import { VersionApiService } from './version-api.service';
import { VersionResponse } from '../models/latest-version-response.interface';
import { NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';
import { NativeVersionSheetComponent } from '../../components/native-version-sheet/native-version-sheet.component';
import { WebVersion } from '../models/web-version.model';
import { HttpClient } from '@angular/common/http';

declare const window: any;

@Injectable({
  providedIn: 'root',
})
export class VersioningService {
  private swUpdate = inject(SwUpdate);
  private loginStateService = inject(LoginStateService);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private ngxHybridService = inject(NgxHybridService);
  private versionApiService = inject(VersionApiService);
  private ngxBottomSheetService = inject(NgxBottomSheetService);
  private storageService = inject(StorageService);
  private http = inject(HttpClient);
  private readonly checkPeriodTime = 60 * 1000;
  public isUpdating = signal(false);
  private safeRoute: string[] = [
    '/auth/onboarding',
    '/hub',
    '/service/credit/overview',
    '/service/credit/pre-register',
    '/subscription',
    '/subscription/subscription-management',
    '/home',
    '/profile/update',
  ];

  constructor(@Inject('APP_ENV') private environment: { [key: string]: string }) {
    if (parseInt(this.environment['version_check_period_seconds'])) {
      this.checkPeriodTime = parseInt(this.environment['version_check_period_seconds']) * 1000;
    }
  }

  public run() {
    if (!this.swUpdate.isEnabled) {
      console.warn('SERVICE WORKER IS NOT ENABLED');
      return;
    }
    this.invoke();
    this.checkForUpdate();
  }
  public checkHybridVersion(): void {
    if (!this.ngxHybridService.isAndroidHybrid() || this.isUpdating()) return;
    this.versionApiService.getNativeVersionApi().subscribe({
      next: (res: VersionResponse) => {
        if (!res.latest && this.checkVersionTtl()) {
          this.isUpdating.set(true);
          this.ngxBottomSheetService.openBottomSheet(
            NativeVersionSheetComponent,
            { newVersion: res?.latestVersion },
            { disableClose: true },
          );
          const sheetSub = this.ngxBottomSheetService.onClose.subscribe(() => {
            sheetSub.unsubscribe();
            const result = this.ngxBottomSheetService.outputData();
            if (result && result.isAllowed) {
              // Navigate user to the app store to get the latest version
              window.open(res?.storeUrl, '_blank');
            }
            this.storageService.setNativeUpdateTimeStamp(Date.now());
            this.isUpdating.set(false);
          });
        }
      },
    });
  }
  invoke() {
    try {
      this.swUpdate.versionUpdates.subscribe({
        next: (versionState: VersionEvent) => {
          this.checkRouteIsSafe().then(() => {
            if (this.isUpdating() || window.isGettingPassword) return;
            if (versionState.type === 'VERSION_READY') {
              this.isUpdating.set(true);
              this.messageService.showInfoMessage('در حال دریافت جدیدترین تغییرات...');
              setTimeout(() => {
                this.reloadPage();
              }, 3000);
              return;
            }
          });
        },
      });
    } catch (e) {
      console.error('versioning service: ', e);
      this.isUpdating.set(false);
    }
  }

  private checkVersionTtl(ttl = 1000 * 60 * 60 * 24 * 2): boolean {
    const lastTime = this.storageService.getNativeUpdateTimeStamp();
    if (!lastTime) {
      return true;
    }
    const now = Date.now();
    return now - lastTime >= ttl;
  }

  checkForUpdate(): void {
    const everyPeriodTime = interval(this.checkPeriodTime);
    everyPeriodTime.subscribe(async () => {
      try {
        const updateFound = await this.swUpdate.checkForUpdate();
        console.log(updateFound ? 'A new version is available.' : 'Already on the latest version.');
      } catch (err) {
        console.error('Failed to check for updates:', err);
      }
    });
  }

  hasNewVersion(): Observable<boolean> {
    return new Observable((subscriber) => {
      this.swUpdate
        .checkForUpdate()
        .then((updateFound) => {
          subscriber.next(updateFound);
          subscriber.complete();
        })
        .catch((err) => {
          console.error('Failed to check for updates:', err);
          subscriber.error(err);
          subscriber.complete();
        });
    });
  }
  /**
   * Returns an observable that emits the current and new version of the app
   * from the Angular service worker's version updates, using appData from ngsw-config.json.
   */
  getWebVersion(): Observable<WebVersion | null> {
    return combineLatest([this.getCurrentWebVersion(), this.watchForNewWebVersion()]).pipe(
      map(([currentVersion, newVersion]) => ({ currentVersion, newVersion })),
    );
  }
  /**
   * Fetches the current app version from the Angular Service Worker's `ngsw.json` file.
   *
   * The version is read from the `appData.version` property inside `ngsw.json`.
   */
  getCurrentWebVersion(): Observable<string | null> {
    return this.http.get<any>('/ngsw.json').pipe(
      map((data) => data?.appData?.version || null),
      catchError(() => of(null)),
    );
  }
  /**
   * Observes the first new version available from the Service Worker.
   * Emits the version string or null initially.
   */
  watchForNewWebVersion(): Observable<string | null> {
    return this.swUpdate.versionUpdates.pipe(
      filter((event): event is Extract<VersionEvent, { type: 'VERSION_READY' }> => event.type === 'VERSION_READY'),
      first(),
      map((event: any) => event.latestVersion.appData?.version || null),
      startWith(null),
    );
  }
  private isLoginRoute(): boolean {
    const currentUrl = window.location.pathname;
    const loginRoute = ['/auth/login'];
    return loginRoute.indexOf(currentUrl) >= 0;
  }
  private checkRouteIsSafe(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.loginStateService.state() === LoginState.PHONENUMBER && this.isLoginRoute()) {
        resolve(true);
      }
      const currentUrl = window.location.pathname;
      const isSafe =
        this.safeRoute.indexOf(currentUrl) >= 0 || currentUrl.startsWith('/mini-app/wealth') || currentUrl.startsWith('/service/bnpl');
      if (isSafe) {
        resolve(true);
      }
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const endUrl = this.router.url;
          if (!this.safeRoute.includes(endUrl)) {
            return;
          }
          resolve(true);
        }
      });
    });
  }
  reloadPage(): void {
    if (!window.activeRequests || window.lastApiCallTime < new Date().getTime() - 60 * 1000) {
      document.location.reload();
      return;
    }
    setTimeout(() => {
      this.reloadPage();
    }, 300);
  }
}
